import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useRxCollection } from 'rxdb/plugins/react';
import type {
    FindingDocType,
    FindingPlanumDocType,
    FindingProfileDocType,
    MediaDocType,
    MediaLinkDocType,
    MediaGroupDocType,
    OverallDescriptionDocType,
    ProjectDocType,
    TranslationDocType
} from '@ast-data/shared';
import { useToast } from '../../../components/toastContext';
import { downloadBlob } from '../../../utils/downloadFile';
import { confirmUnavailableMedia } from '../pdf/confirmUnavailableMedia';
import { useConfirm } from '../../../components/confirmContext';
import { createDeliverableTexts } from '../../translation/deliverableTexts';
import { confirmTranslationGaps } from '../../translation/confirmTranslationGaps';
import {
    collectOverallDescriptionExportData,
    overallDescriptionPdfFileName,
    type OverallDescriptionExportCollections
} from './exportData';
import { usePersonLabels } from '../../users/personLabels';
import { recordMeasureInfo } from '../../projects/projectTitle';

/**
 * Export Gesamtbeschreibungen to a single PDF and download it.
 *
 * `exportPdf(ids)` renders the given records, whatever Maßnahme each is from:
 * every sheet names its own in the head, so the project grid and the
 * cross-project Auswertung use this the same way. `busy` is true while
 * rendering, so a toolbar can show a spinner.
 *
 * i18n comes from the app's `useIntl`, passed into the react-pdf document
 * (which renders outside the IntlProvider).
 *
 * No render-in-parts path here, unlike the Befund documentation: a Maßnahme has
 * a handful of Gesamtbeschreibungen where it has hundreds of Befunde. If that
 * stops being true, findingPdf/renderInParts is the recipe.
 */
export function useOverallDescriptionPdfExport() {
    const intl = useIntl();
    const { notify } = useToast();
    const { confirm } = useConfirm();
    const [busy, setBusy] = useState(false);

    // The sheets name people by their accounts, as the screens do.
    const personLabels = usePersonLabels();
    const projects = useRxCollection<ProjectDocType>('projects');
    const overallDescriptions = useRxCollection<OverallDescriptionDocType>(
        'overall_descriptions'
    );
    const findings = useRxCollection<FindingDocType>('findings');
    const planums = useRxCollection<FindingPlanumDocType>('finding_planums');
    const profiles = useRxCollection<FindingProfileDocType>('finding_profiles');
    const media = useRxCollection<MediaDocType>('media');
    const mediaLinks = useRxCollection<MediaLinkDocType>('media_links');
    const mediaGroups = useRxCollection<MediaGroupDocType>('media_groups');
    const translations = useRxCollection<TranslationDocType>('translations');

    const exportPdf = useCallback(
        async (ids: readonly string[]) => {
            if (
                !projects ||
                !overallDescriptions ||
                !findings ||
                !planums ||
                !profiles ||
                !media ||
                !mediaLinks ||
                !mediaGroups ||
                ids.length === 0
            ) {
                return;
            }
            setBusy(true);
            try {
                const collections: OverallDescriptionExportCollections = {
                    projects,
                    overallDescriptions,
                    findings,
                    planums,
                    profiles,
                    media,
                    mediaLinks,
                    mediaGroups
                };
                const deliverable = createDeliverableTexts(
                    translations,
                    projects
                );
                const data = await collectOverallDescriptionExportData({
                    collections,
                    ids,
                    labelOf: personLabels.labelOf,
                    deliverable
                });
                // Nothing printable: either the records are gone, or their
                // Maßnahme is not on this device — and a sheet's whole head
                // comes from the project.
                if (data.overallDescriptions.length === 0) {
                    notify(
                        intl.formatMessage({
                            id: 'overallDescriptionPdf.empty'
                        }),
                        'info'
                    );
                    return;
                }
                // Offline, pictures not yet on this device cannot print — ask
                // before rendering a PDF without them.
                if (
                    !(await confirmUnavailableMedia(
                        confirm,
                        intl,
                        data.overallDescriptions.flatMap((entry) => entry.media)
                    ))
                ) {
                    return;
                }
                if (
                    !(await confirmTranslationGaps(
                        confirm,
                        intl,
                        deliverable.gaps
                    ))
                ) {
                    return;
                }
                // The renderer arrives here, at the click — see renderPdf.
                const { renderOverallDescriptionPdf } =
                    await import('./renderPdf');
                const blob = await renderOverallDescriptionPdf(data, intl);
                downloadBlob(
                    blob,
                    overallDescriptionPdfFileName(
                        data.overallDescriptions.map(
                            (entry) =>
                                recordMeasureInfo(entry.project, {
                                    recordedAt: entry.doc.recordedAt
                                }).measureNumber
                        ),
                        data.overallDescriptions.map(
                            (entry) => entry.doc.overallDescriptionNumber
                        )
                    )
                );
            } catch (cause) {
                notify(
                    intl.formatMessage({ id: 'overallDescriptionPdf.error' }),
                    'error'
                );
                console.error('Gesamtbeschreibung PDF export failed', cause);
            } finally {
                setBusy(false);
            }
        },
        [
            personLabels,
            projects,
            overallDescriptions,
            findings,
            planums,
            profiles,
            media,
            mediaLinks,
            mediaGroups,
            translations,
            intl,
            notify,
            confirm
        ]
    );

    return { exportPdf, busy };
}
